import ExpandMore from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useState } from "react";

import type { ScoutProposalResponseStatus } from "./ScoutProposalContext";

import {
  formatDataChangeFieldValue,
  formatDataChangeProposalTitle,
  formatDataChangeResponseStatus,
  getDisplayDiffFields,
  isComplexDataChangeProposal,
  normalizeDataChangeDisplayVerb,
  type ScoutDataChangeProposal,
} from "./scoutDataChangeProposal";

type Props = {
  proposal: ScoutDataChangeProposal;
  /** Resolved status from history or deferred local stack decisions. */
  responseStatus?: ScoutProposalResponseStatus;
  /**
   * When true, hide per-card Accept/Reject (reserved for future stack-only
   * chrome). Leave false so CTAs appear.
   */
  hideActions?: boolean;
  /** Disable Accept/Reject while a flush / sibling action is in flight. */
  actionsDisabled?: boolean;
  onAccept: () => Promise<void>;
  onReject: () => Promise<void>;
};

type SubmittingAction = "confirm" | "decline" | null;

const SAMPLE_PREVIEW_COUNT = 3;

const DIFF_GRID_COLUMNS = "minmax(96px, 30%) minmax(0, 1fr) minmax(0, 1fr)";

function DiffValueCell({
  value,
  emphasize = false,
  muted = false,
  placeholder = false,
}: {
  value?: unknown;
  emphasize?: boolean;
  muted?: boolean;
  placeholder?: boolean;
}) {
  const text = value === undefined ? "—" : formatDataChangeFieldValue(value);
  return (
    <Typography
      variant="body2"
      color={muted || placeholder || value === undefined ? "text.secondary" : "text.primary"}
      sx={{
        fontWeight: emphasize ? 600 : 400,
        wordBreak: "break-word",
        textDecoration: muted ? "line-through" : "none",
        fontStyle: placeholder && value === undefined ? "italic" : "normal",
      }}
      title={text}
    >
      {text}
    </Typography>
  );
}

function DiffFieldRow({
  label,
  before,
  after,
  isCreate,
}: {
  label: string;
  before?: unknown;
  after?: unknown;
  isCreate: boolean;
}) {
  const hasBefore = before !== undefined;
  const valuesDiffer =
    hasBefore &&
    after !== undefined &&
    formatDataChangeFieldValue(before) !== formatDataChangeFieldValue(after);

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: DIFF_GRID_COLUMNS,
        columnGap: 1.5,
        alignItems: "start",
        minWidth: 0,
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontWeight: 500, pt: 0.15, wordBreak: "break-word" }}
      >
        {label}
      </Typography>
      <DiffValueCell
        value={isCreate ? undefined : before}
        muted={valuesDiffer}
        placeholder={isCreate || !hasBefore}
      />
      <DiffValueCell value={after} emphasize={valuesDiffer || isCreate} />
    </Box>
  );
}

function DiffColumnHeaders() {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: DIFF_GRID_COLUMNS,
        columnGap: 1.5,
        minWidth: 0,
        pb: 0.5,
        borderBottom: 1,
        borderColor: "divider",
      }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
        Field
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
        Current
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
        Proposed
      </Typography>
    </Box>
  );
}

function SampleList({
  samples,
  caption,
}: {
  samples: Array<{ id: string; label: string }>;
  caption?: string;
}) {
  const preview = samples.slice(0, SAMPLE_PREVIEW_COUNT);
  const remainder = samples.slice(SAMPLE_PREVIEW_COUNT);

  return (
    <Box>
      {caption ? (
        <Typography variant="caption" color="text.secondary" display="block">
          {caption}
        </Typography>
      ) : null}
      {preview.length > 0 ? (
        <Typography variant="body2" sx={{ mt: caption ? 0.25 : 0 }}>
          {preview.map((sample) => sample.label).join(", ")}
          {remainder.length > 0 ? "…" : ""}
        </Typography>
      ) : null}
      {remainder.length > 0 ? (
        <Accordion
          disableGutters
          elevation={0}
          sx={{
            bgcolor: "transparent",
            "&::before": { display: "none" },
            mt: 0.5,
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMore fontSize="small" />}
            sx={{ minHeight: 32, px: 0, "& .MuiAccordionSummary-content": { my: 0.5 } }}
          >
            <Typography variant="caption" color="primary">
              View all ({samples.length})
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 0, pt: 0 }}>
            <Stack spacing={0.5}>
              {samples.map((sample) => (
                <Typography key={sample.id} variant="body2" color="text.secondary">
                  {sample.label}
                </Typography>
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>
      ) : null}
    </Box>
  );
}

function AffectedEntitiesBlock({ proposal }: { proposal: ScoutDataChangeProposal }) {
  const items = proposal.items;
  if (!items || items.length === 0) return null;

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
        Affected ({items.length})
      </Typography>
      <SampleList samples={items} />
    </Box>
  );
}

function ImpactBlock({ proposal }: { proposal: ScoutDataChangeProposal }) {
  const impact = proposal.impact;
  if (!impact) return null;

  return (
    <Box
      sx={(theme) => ({
        px: 1.5,
        py: 1.25,
        borderRadius: 2,
        bgcolor: alpha(theme.palette.warning.main, 0.08),
        border: 1,
        borderColor: alpha(theme.palette.warning.main, 0.24),
      })}
    >
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {impact.headline}
      </Typography>
      <Stack spacing={1} sx={{ mt: 1 }}>
        {impact.related.map((related) => (
          <SampleList
            key={`${related.entityType}-${related.count}`}
            samples={related.samples ?? []}
            caption={`${related.entityType}: ${related.count}`}
          />
        ))}
      </Stack>
    </Box>
  );
}

function ProposalBody({
  proposal,
  showStaffDetails,
  readOnly = false,
}: {
  proposal: ScoutDataChangeProposal;
  showStaffDetails: boolean;
  readOnly?: boolean;
}) {
  const isComplex = isComplexDataChangeProposal(proposal);
  // Diff rows come from the staged proposal (agent/server). Do not fetch or
  // invent "current" values on the client — that duplicates Scout's propose path.
  const displayFields = getDisplayDiffFields(proposal);
  const hasFields = displayFields.length > 0;
  const isCreate = normalizeDataChangeDisplayVerb(proposal.diff.verb) === "Create";

  let emptyCopy = "Review this proposed change, then Accept or Reject.";
  if (readOnly) {
    emptyCopy = "No field details were recorded for this change.";
  }

  return (
    <Stack spacing={1.5}>
      {hasFields ? (
        <Stack spacing={1}>
          <DiffColumnHeaders />
          {displayFields.map((field) => (
            <DiffFieldRow
              key={field.label}
              label={field.label}
              before={field.before}
              after={field.after}
              isCreate={isCreate}
            />
          ))}
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary">
          {emptyCopy}
        </Typography>
      )}
      <AffectedEntitiesBlock proposal={proposal} />
      {isComplex ? <ImpactBlock proposal={proposal} /> : null}
      {showStaffDetails ? (
        <Accordion
          disableGutters
          elevation={0}
          sx={{
            bgcolor: "transparent",
            "&::before": { display: "none" },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMore fontSize="small" />}
            sx={{ minHeight: 36, px: 0, "& .MuiAccordionSummary-content": { my: 0.5 } }}
          >
            <Typography variant="caption" color="text.secondary">
              GraphQL details (staff)
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 0, pt: 0 }}>
            <Typography
              component="pre"
              variant="caption"
              color="text.secondary"
              sx={{
                m: 0,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
              }}
            >
              {proposal.operation.graphql}
            </Typography>
          </AccordionDetails>
        </Accordion>
      ) : null}
    </Stack>
  );
}

/**
 * One staged change card: semantic title, field diffs, optional impact,
 * Accept/Reject. After decision, collapses to an expandable read-only summary.
 */
export function ScoutDataChangeProposalCard({
  proposal,
  responseStatus,
  hideActions = false,
  actionsDisabled = false,
  onAccept,
  onReject,
}: Props) {
  const { isStaff } = useCurrentUser();
  const [hasError, setHasError] = useState(false);
  const [submittingAction, setSubmittingAction] = useState<SubmittingAction>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  const isSubmitting = submittingAction !== null;
  const isLockedOut = isSubmitting || actionsDisabled;
  const title = formatDataChangeProposalTitle(proposal);

  const handleConfirm = async () => {
    if (isSubmitting || actionsDisabled) return;
    setHasError(false);
    setSubmittingAction("confirm");
    try {
      await onAccept();
      setSubmittingAction(null);
    } catch {
      setHasError(true);
      setSubmittingAction(null);
    }
  };

  const handleDecline = async () => {
    if (isSubmitting || actionsDisabled) return;
    setHasError(false);
    setSubmittingAction("decline");
    try {
      await onReject();
      setSubmittingAction(null);
    } catch {
      setHasError(true);
      setSubmittingAction(null);
    }
  };

  if (responseStatus) {
    const statusLabel = formatDataChangeResponseStatus(responseStatus);
    return (
      <Box
        data-testid="scout-data-change-proposal-responded"
        sx={{
          borderRadius: 2,
          border: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
          overflow: "hidden",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{
            px: 1.5,
            py: 1,
            cursor: "pointer",
          }}
          onClick={() => setIsExpanded((current) => !current)}
          role="button"
          aria-expanded={isExpanded}
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              setIsExpanded((current) => !current);
            }
          }}
        >
          <Chip
            size="small"
            label={statusLabel}
            color={responseStatus === "confirmed" ? "success" : "default"}
            variant={responseStatus === "confirmed" ? "filled" : "outlined"}
            sx={{ flexShrink: 0 }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 0 }} noWrap>
            {title}
          </Typography>
          <IconButton
            size="small"
            aria-label={isExpanded ? "Collapse change details" : "Expand change details"}
            sx={{
              transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
              transition: (theme) => theme.transitions.create("transform"),
            }}
          >
            <ExpandMore fontSize="small" />
          </IconButton>
        </Stack>
        <Collapse in={isExpanded} unmountOnExit>
          <Box sx={{ px: 1.5, pb: 1.5, pt: 0.5 }}>
            <ProposalBody proposal={proposal} showStaffDetails={isStaff} readOnly />
          </Box>
        </Collapse>
      </Box>
    );
  }

  return (
    <Box
      data-testid="scout-data-change-proposal-card"
      data-complex={isComplexDataChangeProposal(proposal) ? "true" : "false"}
      sx={{
        p: 2,
        borderRadius: 3,
        border: 1,
        borderColor: "divider",
        bgcolor: "background.paper",
      }}
    >
      <Stack spacing={1.5}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        <ProposalBody proposal={proposal} showStaffDetails={isStaff} readOnly={hideActions} />
        {hasError ? (
          <Alert severity="error">
            Something went wrong responding to this change. Please try again.
          </Alert>
        ) : null}
        {hideActions ? null : (
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button
              onClick={handleDecline}
              disabled={isLockedOut}
              startIcon={
                submittingAction === "decline" ? (
                  <CircularProgress size={16} color="inherit" />
                ) : undefined
              }
            >
              Reject
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirm}
              disabled={isLockedOut}
              startIcon={
                submittingAction === "confirm" ? (
                  <CircularProgress size={16} color="inherit" />
                ) : undefined
              }
            >
              Accept
            </Button>
          </Stack>
        )}
      </Stack>
    </Box>
  );
}
